/**
 * Delivery History Table
 * Shows history of digest emails sent to the user
 */

import React, { useState } from 'react';
import {
    Table,
    Tag,
    Space,
    Typography,
    Tooltip,
    Badge,
    Empty
} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    MailOutlined,
    EyeOutlined,
    ClockCircleOutlined,
    WarningOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;

export default function DeliveryHistoryTable({ history, loading }) {
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100']
    });

    const getStatusIcon = (status) => {
        const icons = {
            sent: <MailOutlined style={{ color: '#1890ff' }} />,
            delivered: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
            opened: <EyeOutlined style={{ color: '#722ed1' }} />,
            clicked: <EyeOutlined style={{ color: '#eb2f96' }} />,
            bounced: <WarningOutlined style={{ color: '#faad14' }} />,
            failed: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
            spam: <WarningOutlined style={{ color: '#ff7a45' }} />
        };
        return icons[status] || <ClockCircleOutlined />;
    };

    const getStatusTag = (emailStatus) => {
        const colors = {
            sent: 'blue',
            delivered: 'green',
            opened: 'purple',
            clicked: 'magenta',
            bounced: 'orange',
            failed: 'red',
            spam: 'volcano'
        };

        const labels = {
            sent: 'Sent',
            delivered: 'Delivered',
            opened: 'Opened',
            clicked: 'Clicked',
            bounced: 'Bounced',
            failed: 'Failed',
            spam: 'Spam'
        };

        return (
            <Tag icon={getStatusIcon(emailStatus)} color={colors[emailStatus]}>
                {labels[emailStatus] || emailStatus}
            </Tag>
        );
    };

    const getHealthScoreBadge = (score) => {
        if (score === null || score === undefined) {
            return <Text type="secondary">N/A</Text>;
        }

        let status = 'success';
        let color = '#52c41a';

        if (score < 50) {
            status = 'error';
            color = '#ff4d4f';
        } else if (score < 70) {
            status = 'warning';
            color = '#faad14';
        }

        return (
            <Badge
                count={score}
                style={{
                    backgroundColor: color,
                    fontSize: '14px',
                    fontWeight: 'bold'
                }}
                overflowCount={100}
            />
        );
    };

    const columns = [
        {
            title: 'Module',
            dataIndex: 'ModuleName',
            key: 'module',
            width: 180,
            fixed: 'left',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Subject',
            dataIndex: 'EmailSubject',
            key: 'subject',
            width: 300,
            ellipsis: {
                showTitle: false
            },
            render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            )
        },
        {
            title: 'Health Score',
            dataIndex: 'HealthScore',
            key: 'healthScore',
            width: 120,
            align: 'center',
            sorter: (a, b) => (a.HealthScore || 0) - (b.HealthScore || 0),
            render: (score) => getHealthScoreBadge(score)
        },
        {
            title: 'Status',
            dataIndex: 'EmailStatus',
            key: 'status',
            width: 120,
            filters: [
                { text: 'Sent', value: 'sent' },
                { text: 'Delivered', value: 'delivered' },
                { text: 'Opened', value: 'opened' },
                { text: 'Failed', value: 'failed' },
                { text: 'Bounced', value: 'bounced' }
            ],
            onFilter: (value, record) => record.EmailStatus === value,
            render: (status) => getStatusTag(status)
        },
        {
            title: 'Sent At',
            dataIndex: 'SentAt',
            key: 'sentAt',
            width: 180,
            sorter: (a, b) => moment(a.SentAt).unix() - moment(b.SentAt).unix(),
            defaultSortOrder: 'descend',
            render: (date) => {
                if (!date) return <Text type="secondary">-</Text>;
                return (
                    <Tooltip title={moment(date).format('LLLL')}>
                        <Space direction="vertical" size={0}>
                            <Text>{moment(date).format('MMM D, YYYY')}</Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {moment(date).format('h:mm A')}
                            </Text>
                        </Space>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Delivered At',
            dataIndex: 'DeliveredAt',
            key: 'deliveredAt',
            width: 180,
            render: (date) => {
                if (!date) return <Text type="secondary">-</Text>;
                return (
                    <Tooltip title={moment(date).format('LLLL')}>
                        <Space direction="vertical" size={0}>
                            <Text>{moment(date).format('MMM D, YYYY')}</Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {moment(date).format('h:mm A')}
                            </Text>
                        </Space>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Opened At',
            dataIndex: 'OpenedAt',
            key: 'openedAt',
            width: 180,
            render: (date) => {
                if (!date) return <Text type="secondary">-</Text>;
                return (
                    <Tooltip title={moment(date).format('LLLL')}>
                        <Space direction="vertical" size={0}>
                            <Text>{moment(date).format('MMM D, YYYY')}</Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {moment(date).format('h:mm A')}
                            </Text>
                        </Space>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Processing Time',
            dataIndex: 'ProcessingTimeMs',
            key: 'processingTime',
            width: 130,
            align: 'right',
            sorter: (a, b) => (a.ProcessingTimeMs || 0) - (b.ProcessingTimeMs || 0),
            render: (ms) => {
                if (!ms) return <Text type="secondary">-</Text>;
                const seconds = (ms / 1000).toFixed(2);
                return (
                    <Tooltip title={`${ms}ms`}>
                        <Text>{seconds}s</Text>
                    </Tooltip>
                );
            }
        }
    ];

    if (!history || history.length === 0) {
        return (
            <Empty
                description="No delivery history yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
                <Text type="secondary">
                    Subscribe to modules to start receiving digest emails
                </Text>
            </Empty>
        );
    }

    return (
        <Table
            columns={columns}
            dataSource={history}
            loading={loading}
            rowKey="DeliveryLogId"
            pagination={pagination}
            onChange={(newPagination) => setPagination(newPagination)}
            scroll={{ x: 1400 }}
            size="middle"
        />
    );
}
